import { Vec3 } from '../../engine/math/vec3';
import { clamp, easeOutCubic, easeInQuad, DEG } from '../../engine/math/easing';
import { sphere, combine } from '../../engine/geo/primitives';
import { setEmissive, scale as scaleMesh, translate } from '../../engine/geo/mesh';
import type { Color } from '../../engine/geo/mesh';
import type { Ctx } from '../Ctx';
import type { Player } from '../player/Player';
import type { Particles } from '../fx/Particles';
import { Beast, BeastPart } from './Beast';

const COUNT = 12;
const COLOR: Color = [0.2, 0.8, 0.72];

/**
 * Scarab Swarm (§5): 12 oblate spheroids orbiting a shared centre that drifts toward the player at walk
 * speed. Contract: tighten 0.5 s, snap to the player and expand (1 heart inside 2.5 m). Each sword hit
 * knocks out two scarabs; six hits total. Slides down dune faces like the player.
 */
export class Scarab extends Beast {
  private readonly center: Vec3;
  private readonly vel = new Vec3();
  private orbitR = 2.2;
  private orbitSpeed = 2.2;
  private readonly angles: number[] = [];
  private readonly phases: number[] = [];
  private snapFrom = new Vec3();
  private snapTo = new Vec3();
  private hitDone = false;
  private aliveParts: BeastPart[] = [];

  constructor(ctx: Ctx, pos: Vec3) {
    super(ctx, 'scarab', pos, 6, COLOR, 70);
    this.center = this.root.position;
    for (let i = 0; i < COUNT; i++) {
      const m = scaleMesh(sphere(0.3, 8, 5, COLOR), 1, 0.5, 1.15);
      setEmissive(m, [0.1, 0.5, 0.6], 0.35);
      const shell = setEmissive(translate(scaleMesh(sphere(0.12, 6, 4, [0.9, 0.3, 0.6]), 1, 0.4, 1), 0, 0.14, 0.05), [0.6, 0.2, 0.5], 0.5);
      const part = this.addPart('scarab.' + i, combine(m, shell), this.root, new Vec3(), 0.35);
      this.angles.push((i / COUNT) * Math.PI * 2);
      this.phases.push(Math.random() * 6);
      this.aliveParts.push(part);
    }
  }

  protected onKnockback(dir: Vec3): void { this.vel.addScaled(dir, 4); }

  override hurt(from: Vec3, particles: Particles, dmg = 1): boolean {
    const before = this.hp;
    const ok = super.hurt(from, particles, dmg);
    if (ok && this.alive) {
      // each hit destroys two scarabs
      const lost = Math.min(2, this.aliveParts.length);
      for (let k = 0; k < lost; k++) {
        const p = this.aliveParts.pop()!;
        particles.burst(p.t.getWorldPosition(_v), 6, COLOR, 4, 0.14, 0.7, 0.5);
        p.rd.visible = false;
      }
    }
    void before;
    return ok;
  }

  update(dt: number, player: Player): void {
    this.tick(dt);
    if (!this.alive) return;
    const st = this.ctx.statics;
    const engaged = this.updateAggro(player, dt);
    const c = this.center;
    const toP = _v.copy(player.position).sub(c); toP.y = 0;
    const dist = toP.length();
    const groundY = st.terrainHeight(c.x, c.z);

    switch (this.state) {
      case 'idle':
      case 'alert': {
        const wantSpeed = engaged ? 3.6 * this.speedMul : 0;
        if (engaged && dist > 0.5) this.vel.addScaled(toP.normalize(), 8 * dt);
        this.vel.clampLength(wantSpeed || 0.8);
        this.vel.scale(1 - 1.5 * dt);
        // slide down steep faces (§5)
        const n = _n; st.heightfieldAt(c.x, c.z)?.normal(c.x, c.z, n);
        const slope = Math.acos(clamp(n.y, -1, 1));
        if (slope > 35 * DEG) { const l = Math.hypot(n.x, n.z); if (l > 1e-4) this.vel.addScaled(_d.set(n.x / l, 0, n.z / l), 10 * dt); }
        c.x += this.vel.x * dt; c.z += this.vel.z * dt;
        st.resolveCapsule(c, 0.6, 1.0, 0.4);
        this.orbitR += (2.2 - this.orbitR) * 3 * dt;
        this.orbitSpeed = engaged ? 3.2 : 1.6;
        this.state = engaged ? 'alert' : 'idle';
        if (engaged && dist < 6 && this.stateT > 1.2) {
          this.setState('telegraph');
          this.ctx.sfx.telegraph('scarab', c, 0.5);
        }
        break;
      }
      case 'telegraph': {
        const k = clamp(this.stateT / 0.5, 0, 1);
        this.orbitR = 2.2 - 1.5 * easeInQuad(k);
        this.orbitSpeed = 3 + k * 12;
        this.vel.scale(1 - 4 * dt);
        if (k >= 1) {
          this.setState('attack');
          this.snapFrom.copy(c);
          this.snapTo.copy(player.position);
          this.hitDone = false;
        }
        break;
      }
      case 'attack': {
        const k = clamp(this.stateT / 0.14, 0, 1);
        const e = easeOutCubic(k);
        c.x = this.snapFrom.x + (this.snapTo.x - this.snapFrom.x) * e;
        c.z = this.snapFrom.z + (this.snapTo.z - this.snapFrom.z) * e;
        this.orbitR = 0.7 + e * 2.5;
        this.orbitSpeed = 14;
        if (!this.hitDone && k > 0.6) {
          this.hitDone = true;
          const hc = _d.copy(c); hc.y = player.position.y + 0.8;
          this.tryHitPlayer(player, hc, 2.5 - 0.32, 2.5);
          this.ctx.fx.shake(0.1, 0.2);
        }
        if (k >= 1) this.setState('recover');
        break;
      }
      case 'recover': {
        this.orbitR += (3.2 - this.orbitR) * 4 * dt;
        this.orbitSpeed = 2;
        if (this.stateT >= 1.0) this.setState('alert');
        break;
      }
      default: break;
    }
    c.y += (groundY + 1.3 - c.y) * 6 * dt;

    // orbit the parts; oblate spheroids tilt into their motion
    const t = this.ctx.time;
    for (let i = 0; i < COUNT; i++) {
      const p = this.parts[i];
      if (!p.rd.visible) continue;
      this.angles[i] += this.orbitSpeed * dt * (1 + (i % 3) * 0.15);
      const a = this.angles[i], ph = this.phases[i];
      const r = this.orbitR * (0.8 + 0.2 * Math.sin(t * 2 + ph));
      p.t.position.set(Math.cos(a) * r, Math.sin(t * 3 + ph) * 0.35 + (i % 2) * 0.3, Math.sin(a) * r);
      p.t.rotation.setEuler(-a, 0.3, Math.sin(t * 20 + ph) * 0.4);
    }
  }
}
const _v = new Vec3(), _d = new Vec3(), _n = new Vec3(0, 1, 0);
